import { ofType } from 'redux-observable';
import { filter, mergeMap, of, map } from 'rxjs';
import { ConfDefnComponentType, CollectionAnalysisComponent } from 'configuration-schemas/confdefnComponents';
import { AppEpic } from 'src/store';
import {
  inputIsNotNullOrUndefined,
  isScopeDefined,
  maybeGetComponentProps,
  topMemInWorklistSelected,
} from 'src/utils/Functions/epicsFunctions';
import { receiveScopeRefreshTrigger } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { setActivePage, setActiveSubPage } from 'src/pages/NavigationShell/NavigationShell.slice';
import { receiveWorklistConfig, updateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import {
  fetchCollectionAnalysis,
  fetchCollectionAnalysisItems,
  fetchCollectionAnalysisConfigs,
  fetchCollectionAnalysisAnchorData,
  fetchCollectionAnalysisClusterData,
  receiveCollectionAnalysisConfig,
  requestCollectionAnalysisConfig,
} from './CollectionAnalysis.slice';
import { isNil } from 'lodash';
import { ViewDefnState } from 'src/dao/tenantConfigClient';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { ListDataOptions } from 'src/worker/pivotWorker.types';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from 'src/pages/AssortmentBuild/Planning.types';

export const collectionAnalysisConfigLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(setActivePage.type, setActiveSubPage.type, receiveWorklistConfig.type, requestCollectionAnalysisConfig.type),
    map(() =>
      maybeGetComponentProps<CollectionAnalysisComponent>(state$.value, ConfDefnComponentType.collectionAnalysis),
    ),
    filter(inputIsNotNullOrUndefined),
    filter(() => {
      // config fetch can be triggered by worklist or by standalone component request
      // only fetch if we haven't fetched already regardless of who triggered it
      const slice = state$.value.pages.assortmentBuild.collectionAnalysis;
      return isNil(slice.viewDefn) && slice.viewDefnState === ViewDefnState.idle;
    }),
    mergeMap(({ defns }) => {
      return of(fetchCollectionAnalysisConfigs(defns));
    }),
  );
};

export const collectionAnalysisDataLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      receiveCollectionAnalysisConfig.type,
      updateSelectedItem.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
    ),
    map(() =>
      maybeGetComponentProps<CollectionAnalysisComponent>(state$.value, ConfDefnComponentType.collectionAnalysis),
    ),
    filter(inputIsNotNullOrUndefined),
    filter(() => isScopeDefined(state$.value.scope)),
    filter(() => topMemInWorklistSelected(state$.value)),
    mergeMap(({ anchorGridDataApi, clusterGridDataApi, collectionDataApi, itemsDataApi, macroDataApi, topMembers }) => {
      const collectionOptions: ListDataOptions = {
        aggBy: collectionDataApi.params.aggBy,
        topMembers,
      };
      const itemsOptions: ListDataOptions = {
        aggBy: itemsDataApi.params.aggBy,
        topMembers,
      };
      const anchorOptions: ListDataOptions = {
        aggBy: anchorGridDataApi?.params.aggBy,
        topMembers,
      };
      const clusterOptions: ListDataOptions = {
        aggBy: clusterGridDataApi?.params.aggBy,
        topMembers,
      };
      return of(
        fetchCollectionAnalysis(collectionDataApi.defnId, collectionOptions),
        fetchCollectionAnalysisItems(itemsDataApi.defnId, itemsOptions),
        anchorGridDataApi ? fetchCollectionAnalysisAnchorData(anchorGridDataApi.defnId, anchorOptions) : of(),
        clusterGridDataApi ? fetchCollectionAnalysisClusterData(clusterGridDataApi.defnId, clusterOptions) : of(),
        // fetchCollectionAnalysisMacros(macroDataApi.defnId, options),
      );
    }),
  );
};
